import React from 'react';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';

// Every FigmaButtonRow button object must carry the full shape (FigmaButtonRow
// infers the prop type from its full default), so build each from this base.
// href:'' keeps buttons on onClick instead of wrapping them in an <a href='/'>.
const baseButton = {
  className: '',
  iconName: 'fa-solid fa-circle',
  disabled: false,
  label: '',
  type: ButtonTypeEnum.NormalNormal,
  color: ButtonColorsEnum.Primary,
  iconPosition: IconPositionEnum.None,
  tooltip: '',
  roleKey: '',
  submit: false,
  onClick: () => {},
  href: '',
  target: '_blank',
  rel: 'noopener noreferrer',
  hidden: false,
};

function ButtonRowDemoComponent() {
  const iconOnlyButtons = [
    {
      ...baseButton,
      iconName: 'fa-solid fa-circle-plus',
      color: ButtonColorsEnum.Primary,
      type: ButtonTypeEnum.NormalIconOnly,
      iconPosition: IconPositionEnum.Center,
      tooltip: 'Create',
    },
    {
      ...baseButton,
      iconName: 'fa-solid fa-users',
      color: ButtonColorsEnum.Secondary,
      type: ButtonTypeEnum.NormalIconOnly,
      iconPosition: IconPositionEnum.Center,
      tooltip: 'Assign users',
    },
    {
      ...baseButton,
      iconName: 'fa-solid fa-comment-dots',
      color: ButtonColorsEnum.Common,
      type: ButtonTypeEnum.NormalIconOnly,
      iconPosition: IconPositionEnum.Center,
      tooltip: 'Comments',
    },
  ];

  const labelledButtons = [
    {
      ...baseButton,
      iconName: 'fa-solid fa-floppy-disk',
      label: 'Save',
      color: ButtonColorsEnum.Primary,
      type: ButtonTypeEnum.NormalIconLeft,
      iconPosition: IconPositionEnum.Left,
    },
    {
      ...baseButton,
      iconName: 'fa-solid fa-xmark',
      label: 'Cancel',
      color: ButtonColorsEnum.Common,
      type: ButtonTypeEnum.NormalIconLeft,
      iconPosition: IconPositionEnum.Left,
    },
  ];

  const stateButtons = [
    {
      ...baseButton,
      iconName: 'fa-solid fa-trash',
      label: 'Delete',
      color: ButtonColorsEnum.Destruct,
      type: ButtonTypeEnum.NormalIconLeft,
      iconPosition: IconPositionEnum.Left,
    },
    {
      ...baseButton,
      iconName: 'fa-solid fa-lock',
      label: 'Disabled',
      color: ButtonColorsEnum.Primary,
      type: ButtonTypeEnum.NormalIconLeft,
      iconPosition: IconPositionEnum.Left,
      disabled: true,
    },
  ];

  return (
    <div>
      <div className='dsg-row'>
        <div className='dsg-demo-label'>Icon-only row (grouped)</div>
        <FigmaButtonRow buttons={iconOnlyButtons} className='' />
      </div>
      <div className='dsg-row'>
        <div className='dsg-demo-label'>Icon + label</div>
        <FigmaButtonRow buttons={labelledButtons} className='' />
      </div>
      <div className='dsg-row'>
        <div className='dsg-demo-label'>Destruct & disabled</div>
        <FigmaButtonRow buttons={stateButtons} className='' />
      </div>
    </div>
  );
}

export const buttonRowDemo = {
  id: 'button-row',
  title: 'Button row',
  category: 'Basics',
  importPath: 'src/Elements/FigmaButtonRow.js (renders src/Elements/FigmaButton.js)',
  notes: [
    'FigmaButtonRow is the modern function-component API: pass a buttons array of config objects; each renders one FigmaButton inside a grouped .MenuButtonList / .btn-group.',
    'Each button object: { iconName, label, color, type, iconPosition, tooltip, disabled, onClick, href, roleKey, hidden }.',
    'FigmaButton now renders the Bravo DS button look (identical to the .btn CSS buttons), styled global/unscoped in src/Css/Button.css so it applies on non-bravo-ds pages too.',
    'color comes from ButtonColorsEnum (Primary=red CTA, Secondary=navy, Common=neutral outline, Destruct=red outline; Danger=red; Success/Warning fall back to outline); type from ButtonTypeEnum (NormalIconOnly, NormalIconLeft, …) sets size — icon-only types are square and strip the label.',
    'iconPosition (IconPositionEnum) MUST be set to Left/Right/Center for the icon to render — the default None hides it (unlike legacy ButtonRow, which forces Left automatically).',
    'roleKey gates a button via RoleService.CheckUserPermission(\'ButtonRowButton\', roleKey); leave empty for always-visible. hidden:true drops the button entirely.',
    'Legacy equivalent: src/Elements/ButtonRow.js — a class component driven by parallel arrays (labels[], icons[], colors[], keys[]). Prefer FigmaButtonRow for new work.',
    "Wrapper className defaults to 'MenuButtonList' — a header-toolbar class that floats right and sticks with margin-top:-2000px (list-page placement). Outside that header (like this gallery) pass className='' so the .btn-group renders inline instead of flying off the top of the page.",
  ],
  snippet:
    "<FigmaButtonRow\n  buttons={[\n    { iconName: 'fa-solid fa-circle-plus', color: ButtonColorsEnum.Primary,\n      type: ButtonTypeEnum.NormalIconOnly, iconPosition: IconPositionEnum.Center,\n      tooltip: 'Create', onClick: handleCreate },\n  ]}\n/>",
  render: ButtonRowDemoComponent,
};
